import React, { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Send } from "lucide-react";
import apiClient from "../lib/api";
import { useI18n } from "../i18n";
import { toast } from "sonner";

const Contact = () => {
  const { lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiClient.post("/contact", form);
      toast.success(lang === "nl" ? "Bedankt — we reageren binnen 24 uur." : "Thanks — we'll reply within 24 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error(lang === "nl" ? "Er ging iets mis" : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const content = lang === "nl" ? {
    eyebrow: "Contact",
    title: "Spreek ons. We reageren binnen 24 uur.",
    sub: "Vraag, klacht of compliment — we lezen alles persoonlijk.",
    name: "Naam",
    subject: "Onderwerp",
    message: "Bericht",
    send: "Verstuur",
    whatsapp: "Chat op WhatsApp",
    hours: "Klantenservice ma-vr 9:00 - 17:30",
  } : {
    eyebrow: "Contact",
    title: "Talk to us. We reply within 24 hours.",
    sub: "Question, complaint, or compliment — we read everything personally.",
    name: "Name",
    subject: "Subject",
    message: "Message",
    send: "Send",
    whatsapp: "Chat on WhatsApp",
    hours: "Customer service Mon-Fri 9:00 - 17:30",
  };

  return (
    <div className="max-w-[1300px] mx-auto px-6 lg:px-12 py-16 grid md:grid-cols-12 gap-10" data-testid="contact-page">
      <div className="md:col-span-5">
        <div className="font-label text-terracotta">{content.eyebrow}</div>
        <h1 className="font-display text-5xl md:text-6xl tracking-tighter font-light mt-4 leading-[0.95]">{content.title}</h1>
        <p className="mt-5 text-ink-muted leading-relaxed">{content.sub}</p>

        <div className="mt-10 space-y-5">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-terracotta shrink-0 mt-0.5" />
            <div>
              <div className="font-label text-ink-muted">Email</div>
              <a href="mailto:hello@snapshop.it.com" className="hover:text-terracotta">hello@snapshop.it.com</a>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Phone className="w-5 h-5 text-terracotta shrink-0 mt-0.5" />
            <div>
              <div className="font-label text-ink-muted">Phone</div>
              <a href="tel:+31600000000" className="hover:text-terracotta">+31 6 0000 0000</a>
              <div className="text-xs text-ink-muted mt-1">{content.hours}</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MessageCircle className="w-5 h-5 text-[#25D366] shrink-0 mt-0.5" />
            <div>
              <div className="font-label text-ink-muted">WhatsApp</div>
              <a href="https://wa.me/31600000000" target="_blank" rel="noreferrer" className="hover:text-terracotta">{content.whatsapp}</a>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-terracotta shrink-0 mt-0.5" />
            <div>
              <div className="font-label text-ink-muted">Office</div>
              <div>SnapShop B.V.<br />Prinsengracht 1, 1015 Amsterdam<br />Netherlands</div>
            </div>
          </div>
        </div>
      </div>

      <div className="md:col-span-7">
        <form onSubmit={submit} className="bg-white border border-black/10 p-8 md:p-10 space-y-4" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-4">
            <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={content.name} className="border border-black/15 px-4 py-3 text-sm bg-white" />
            <input data-testid="contact-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="border border-black/15 px-4 py-3 text-sm bg-white" />
          </div>
          <input data-testid="contact-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder={content.subject} className="border border-black/15 px-4 py-3 text-sm bg-white w-full" />
          <textarea data-testid="contact-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={content.message} className="border border-black/15 px-4 py-3 text-sm bg-white w-full" />
          <button data-testid="contact-submit" disabled={busy} className="btn-primary w-full text-sm">{content.send} <Send className="w-4 h-4" /></button>
        </form>
      </div>
    </div>
  );
};

export default Contact;
